<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { debounce } from '@chatwoot/utils';

import ListToolbar from 'dashboard/components-next/polaris/cards/ListToolbar.vue';
import BulkSelectBar from 'dashboard/components-next/polaris/assistant/BulkSelectBar.vue';
import DeleteDialog from 'dashboard/components-next/polaris/pageComponents/DeleteDialog.vue';
import BulkDeleteDialog from 'dashboard/components-next/polaris/pageComponents/BulkDeleteDialog.vue';
import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import ResponseCardSkin from 'dashboard/components-next/polaris/skin/ResponseCardSkin.vue';
import ResponsesTabs from 'dashboard/components-next/polaris/pageComponents/response/ResponsesTabs.vue';
import CreateResponseDialog from 'dashboard/components-next/polaris/pageComponents/response/CreateResponseDialog.vue';
import ResponsePageEmptyState from 'dashboard/components-next/polaris/pageComponents/emptyStates/ResponsePageEmptyState.vue';

const router = useRouter();
const route = useRoute();
const store = useStore();
const uiFlags = useMapGetter('polarisResponses/getUIFlags');
const responseMeta = useMapGetter('polarisResponses/getMeta');
const responses = useMapGetter('polarisResponses/getRecords');
const isFetching = computed(() => uiFlags.value.fetchingList);

const selectedResponse = ref(null);
const deleteDialog = ref(null);
const bulkDeleteDialog = ref(null);

const dialogType = ref('');
const searchQuery = ref('');
const { t } = useI18n();

const createDialog = ref(null);

const selectedAssistantId = computed(() => Number(route.params.assistantId));

const handleDelete = () => {
  deleteDialog.value.dialogRef.open();
};

const handleCreate = () => {
  dialogType.value = 'create';
  nextTick(() => createDialog.value.dialogRef.open());
};

const handleEdit = () => {
  dialogType.value = 'edit';
  nextTick(() => createDialog.value.dialogRef.open());
};

const handleAction = ({ action, id }) => {
  selectedResponse.value = responses.value.find(response => id === response.id);
  nextTick(() => {
    if (action === 'delete') {
      handleDelete();
    }
    if (action === 'edit') {
      handleEdit();
    }
  });
};

// An FAQ that came from a conversation links back to it (the card's source).
const handleNavigationAction = ({ id, type }) => {
  if (type === 'Conversation') {
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: id },
    });
  }
};

const handleCreateClose = () => {
  dialogType.value = '';
  selectedResponse.value = null;
};

const updateURLWithFilters = (page, search) => {
  const query = {
    page: page || 1,
  };

  if (search) {
    query.search = search;
  }

  router.replace({ query });
};

const fetchResponses = (page = 1) => {
  const filterParams = { page, status: 'approved' };

  if (selectedAssistantId.value) {
    filterParams.assistantId = selectedAssistantId.value;
  }
  if (searchQuery.value) {
    filterParams.search = searchQuery.value;
  }

  // Update URL with current filters
  updateURLWithFilters(page, searchQuery.value);

  store.dispatch('polarisResponses/get', filterParams);
};

// Bulk action
const bulkSelectedIds = ref(new Set());

const buildSelectedCountLabel = computed(() => {
  const count = responses.value?.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('POLARIS.RESPONSES.UNSELECT_ALL', { count })
    : t('POLARIS.RESPONSES.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('POLARIS.RESPONSES.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const handleCardSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const fetchResponseAfterBulkAction = () => {
  const hasNoResponsesLeft = responses.value?.length === 0;
  const currentPage = responseMeta.value?.page;

  if (hasNoResponsesLeft) {
    // Page is now empty after bulk action.
    // Fetch the previous page if not already on the first page.
    const pageToFetch = currentPage > 1 ? currentPage - 1 : currentPage;
    fetchResponses(pageToFetch);
  } else {
    // Page still has responses left, re-fetch the same page.
    fetchResponses(currentPage);
  }

  // Clear selection
  bulkSelectedIds.value = new Set();
};

const onPageChange = page => {
  const hadSelection = bulkSelectedIds.value.size > 0;

  fetchResponses(page);

  if (hadSelection) {
    bulkSelectedIds.value = new Set();
  }
};

const onDeleteSuccess = () => {
  if (responses.value?.length === 0 && responseMeta.value?.page > 1) {
    onPageChange(responseMeta.value.page - 1);
  }
};

const onBulkDeleteSuccess = () => {
  fetchResponseAfterBulkAction();
};

const debouncedSearch = debounce(async () => {
  fetchResponses(1);
}, 500);

const initializeFromURL = () => {
  if (route.query.search) {
    searchQuery.value = route.query.search;
  }
  const pageFromURL = parseInt(route.query.page, 10) || 1;
  fetchResponses(pageFromURL);
};

onMounted(() => {
  initializeFromURL();
});
</script>

<template>
  <PageLayout
    preview="open"
    :total-count="responseMeta.totalCount"
    :current-page="responseMeta.page"
    :button-policy="['administrator']"
    :header-title="$t('POLARIS.RESPONSES.HEADER')"
    :button-label="$t('POLARIS.RESPONSES.ADD_NEW')"
    :is-fetching="isFetching"
    :is-empty="!responses.length"
    :show-pagination-footer="!isFetching && !!responses.length"
    :feature-flag="FEATURE_FLAGS.POLARIS"
    :invitation="{
      title: $t('POLARIS.RESPONSES.EMPTY_STATE.FEATURE_SPOTLIGHT.TITLE'),
      text: $t('POLARIS.RESPONSES.EMPTY_STATE.FEATURE_SPOTLIGHT.NOTE'),
      actionLabel: $t('POLARIS.RESPONSES.ADD_NEW'),
    }"
    invitation-key="faqs"
    @update:current-page="onPageChange"
    @click="handleCreate"
  >
    <template #tabs>
      <ResponsesTabs />
    </template>

    <template #subHeader>
      <ListToolbar
        v-if="bulkSelectedIds.size === 0"
        v-model:search="searchQuery"
        :placeholder="$t('POLARIS.RESPONSES.SEARCH_PLACEHOLDER')"
        :count="responses.length ? responseMeta.totalCount : null"
        count-key="POLARIS.PAGES.RESPONSES_COUNT"
        @update:search="debouncedSearch"
      />
      <BulkSelectBar
        v-else
        v-model="bulkSelectedIds"
        :all-items="responses"
        :select-all-label="buildSelectedCountLabel"
        :selected-count-label="selectedCountLabel"
        :delete-label="$t('POLARIS.RESPONSES.BULK_DELETE_BUTTON')"
        @bulk-delete="bulkDeleteDialog.dialogRef.open()"
      />
    </template>

    <template #emptyState>
      <ResponsePageEmptyState @click="handleCreate" />
    </template>

    <template #paywall />

    <template #body>
      <div class="flex flex-col gap-4">
        <ResponseCardSkin
          v-for="response in responses"
          :key="response.id"
          :response="response"
          selectable
          :selected="bulkSelectedIds.has(response.id)"
          @action="handleAction"
          @select="handleCardSelect"
          @navigate="handleNavigationAction"
        />
      </div>
    </template>

    <DeleteDialog
      v-if="selectedResponse"
      ref="deleteDialog"
      :entity="selectedResponse"
      type="Responses"
      @delete-success="onDeleteSuccess"
    />

    <BulkDeleteDialog
      v-if="bulkSelectedIds"
      ref="bulkDeleteDialog"
      :bulk-ids="bulkSelectedIds"
      type="AssistantResponse"
      @delete-success="onBulkDeleteSuccess"
    />

    <CreateResponseDialog
      v-if="dialogType"
      ref="createDialog"
      :type="dialogType"
      :selected-response="selectedResponse"
      @close="handleCreateClose"
    />
  </PageLayout>
</template>
